import { forwardRef, useImperativeHandle } from "react";
import { FormattedMessage } from "react-intl";
import { graphql, usePaginationFragment } from "react-relay";
import { Schema } from "./schema";
import * as v from "valibot";
import { Link } from "react-router-dom";
import EntityProfilePic from "../EntityProfilePic";
import LoadMore from "../LoadMore";
import SubmissionStatusBadge from "../SubmissionStatusBadge";
import SubmissionApprovals from "../SubmissionApprovals";
import { MdFolderOpen } from "react-icons/md";
import {
  ProjectVersionApproveButton,
  ProjectVersionRejectButton,
} from "../ProjectVersionApproval";
import { SubmissionsCompetitionApprovalsFragment$key } from "./__generated__/SubmissionsCompetitionApprovalsFragment.graphql";

const Fragment = graphql`
  fragment SubmissionsCompetitionApprovalsFragment on Competition
  @refetchable(queryName: "CompetitionApprovalsFragmentPaginationQuery")
  @argumentDefinitions(
    cursor: { type: "String" }
    count: { type: "Int", defaultValue: 10 }
    approvalFilter: { type: "ApprovalFilter" }
  ) {
    slug
    viewerCanApprove: viewerCan(action: CREATE_PROJECT_VERSION_APPROVAL)
    submissions(approvalFilter: $approvalFilter, after: $cursor, first: $count)
      @connection(key: "CompetitionApprovalsFragment_submissions") {
      edges {
        node {
          id
          entity {
            username
            ...EntityProfilePicFragment @arguments(thumbnail: true)
          }
          latest {
            id
            version
            viewerCanReadFiles: viewerCan(action: READ_PROJECT_VERSION_FILE)
            approvalsCheck: approvals(first: 2) @ifAllowed {
              nodes {
                viewerCanRead: viewerCan(action: READ_PROJECT_VERSION_APPROVAL)
              }
            }
            ...SubmissionStatusBadgeFragment
            ...BaseProjectVersionApprovalButtonFragment
            ...SubmissionApprovalsFragment
          }
        }
      }
    }
  }
`;

export interface SubmissionsProps {
  competition: SubmissionsCompetitionApprovalsFragment$key;
}

export interface SubmissionsHandle {
  refetch(filters: v.InferOutput<Schema>): void;
}

export default forwardRef<SubmissionsHandle, SubmissionsProps>(
  function Submissions(props, ref) {
    const {
      data: competition,
      loadNext,
      hasNext,
      isLoadingNext,
      refetch,
    } = usePaginationFragment(Fragment, props.competition);

    useImperativeHandle(
      ref,
      () => ({
        refetch({ approvalFilter }) {
          refetch({ approvalFilter });
        },
      }),
      [refetch],
    );

    if (competition.submissions.edges.length <= 0) {
      return (
        <p className="p-4 text-gray-400">
          <FormattedMessage defaultMessage="No submissions found" />
        </p>
      );
    }

    const showApprovals = competition.submissions.edges.every(
      ({ node: { latest } }) =>
        latest &&
        latest.approvalsCheck?.nodes.every(
          ({ viewerCanRead }) => viewerCanRead,
        ),
    );

    return (
      <div className="overflow-x-auto">
        <table className="table-auto w-full">
          <thead>
            <tr>
              <th className="py-3 px-3 bg-gray-100 w-[40%]">
                <FormattedMessage defaultMessage="User" />
              </th>
              <th className="py-3 px-3 bg-gray-100">
                <FormattedMessage defaultMessage="Version" />
              </th>
              <th className="py-3 px-3 bg-gray-100">
                <FormattedMessage defaultMessage="Files" />
              </th>
              <th className="py-3 px-3 bg-gray-100">
                <FormattedMessage defaultMessage="Evaluation" />
              </th>
              {showApprovals && (
                <th className="py-3 px-3 bg-gray-100">
                  <FormattedMessage defaultMessage="Reviewers" />
                </th>
              )}
              {competition.viewerCanApprove && (
                <th className="py-3 px-3 bg-gray-100 w-[0.01%]">
                  <FormattedMessage defaultMessage="Review" />
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {competition.submissions.edges.map(
              ({ node: { entity, latest: version, ...node } }) => {
                if (!version) {
                  return null;
                }
                return (
                  <tr key={node.id}>
                    <td className="whitespace-nowrap py-2 px-3">
                      <Link to={`/${entity.username}`} className="inline-block">
                        <div className="flex flex-row min-w-0 items-center hover:cursor-pointer">
                          <div className="pr-4">
                            <EntityProfilePic entity={entity} size="12" />
                          </div>
                          <span>{entity.username}</span>
                        </div>
                      </Link>
                    </td>
                    <td className="text-center py-2 px-3">
                      v{version.version}
                    </td>
                    <td className="text-center py-2 px-3">
                      {version.viewerCanReadFiles && (
                        <Link
                          className="whitespace-nowrap underline"
                          to={`/${entity.username}/submissions/${competition.slug}/${version.version}/code/package`}
                        >
                          <MdFolderOpen className="inline-block mr-1" />
                          <FormattedMessage defaultMessage="Browse" />
                        </Link>
                      )}
                    </td>
                    <td className="whitespace-nowrap py-2 px-3 text-center">
                      <span className="inline-block">
                        <SubmissionStatusBadge projectVersion={version}>
                          <Link
                            className="underline"
                            to={`/${entity.username}/submissions/${competition.slug}/${version.version}/code/package`}
                          >
                            <FormattedMessage
                              defaultMessage="Open code @ v{version}"
                              values={{ version: version.version }}
                            />
                          </Link>
                        </SubmissionStatusBadge>
                      </span>
                    </td>
                    {showApprovals && (
                      <td className="py-2 px-3">
                        <SubmissionApprovals submission={version} />
                      </td>
                    )}
                    {competition.viewerCanApprove && (
                      <td className="whitespace-nowrap py-2 px-3 text-center">
                        <span className="inline-flex gap-2">
                          <ProjectVersionApproveButton
                            projectVersion={version}
                          />
                          <ProjectVersionRejectButton
                            projectVersion={version}
                          />
                        </span>
                      </td>
                    )}
                  </tr>
                );
              },
            )}
          </tbody>
        </table>
        <LoadMore
          loadMore={loadNext}
          hasMore={hasNext}
          isLoading={isLoadingNext}
        />
      </div>
    );
  },
);
